import type { MemberRuntimeItem } from "./local-projection-client";

type GatewayEnvelope<T> = { ok: boolean; data?: T; error?: string };

export type PosMemberSignupResult = {
  ok: boolean;
  contract: string;
  idempotent_replay: boolean;
  result_status: "CREATED" | "EXISTING_MEMBER" | string;
  member: MemberRuntimeItem;
};

export class PosMemberSignupGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  async signup(input: {
    deviceSecret: string;
    sessionToken: string;
    nickname: string;
    phone: string;
    termsAccepted: boolean;
    requestKey: string;
  }): Promise<{ ok: boolean; data: PosMemberSignupResult | null; error: string | null }> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/member-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
          nickname: input.nickname,
          phone: input.phone,
          terms_accepted: input.termsAccepted,
          request_key: input.requestKey,
        }),
        credentials: "omit",
        signal: controller.signal,
      });
      const payload = (await response.json()) as GatewayEnvelope<PosMemberSignupResult>;
      if (!response.ok || !payload.ok || !payload.data) {
        return { ok: false, data: null, error: payload.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      return { ok: true, data: payload.data, error: null };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
